import * as React from 'react';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import classNames from 'classnames';
import useMergedState from 'rc-util/lib/hooks/useMergedState';

import DisabledContext from '../config-provider/DisabledContext';
import { blue, green, lime, pink, purple, yellow } from '../theme/themes/colorPalette';
import { useColorSelectStyle } from './style/color-select';

export interface ColorSelectOption {
  value: string;
  color: string;
  label?: string;
}

export interface ColorSelectProps {
  options?: ColorSelectOption[];
  value?: string;
  defaultValue?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

export interface ColorSelectRef {
  nativeElement: HTMLDivElement;
  focus: VoidFunction;
}

export const DEFAULT_COLOR_OPTIONS: ColorSelectOption[] = [
  { value: 'green', color: green.dark, label: 'Green' },
  { value: 'yellow', color: yellow.medium, label: 'Yellow' },
  { value: 'purple', color: purple.dark, label: 'Purple' },
  { value: 'blue', color: blue[400], label: 'Blue' },
  { value: 'pink', color: pink.dark, label: 'Pink' },
  { value: 'lime', color: lime.dark, label: 'Light Green' },
];

const ColorSelect = React.forwardRef<ColorSelectRef, ColorSelectProps>((props, ref) => {
  const {
    options = DEFAULT_COLOR_OPTIONS,
    value,
    defaultValue,
    disabled: customDisabled,
    onChange,
    id,
    className,
    style,
  } = props;

  const contextDisabled = React.useContext(DisabledContext);
  const disabled = customDisabled ?? contextDisabled;

  const [innerValue, setInnerValue] = useMergedState(defaultValue, { value });
  const divRef = React.useRef<HTMLDivElement>(null);

  React.useImperativeHandle(ref, () => ({
    get nativeElement() {
      return divRef.current!;
    },
    focus: () => divRef.current?.querySelector<HTMLButtonElement>('button')?.focus(),
  }));

  const prefixCls = 'youlify-color-select';
  const [wrapSSR, hashId] = useColorSelectStyle(prefixCls);

  // A disabled <button> already blocks the click handler, so no guard needed here.
  const handleSelect = (next: string) => {
    setInnerValue(next);
    onChange?.(next);
  };

  const containerCls = classNames(prefixCls, hashId, className, {
    [`${prefixCls}-disabled`]: disabled,
  });

  return wrapSSR(
    <div ref={divRef} id={id} className={containerCls} style={style}>
      {options.map((opt) => {
        const selected = opt.value === innerValue;
        return (
          <button
            key={opt.value}
            type="button"
            aria-label={opt.label ?? opt.value}
            aria-pressed={selected}
            disabled={disabled}
            className={classNames(`${prefixCls}-swatch`, hashId, {
              [`${prefixCls}-swatch-selected`]: selected,
            })}
            style={{ background: opt.color }}
            onClick={() => handleSelect(opt.value)}
          >
            {selected && <CheckOutlined className={classNames(`${prefixCls}-tick`, hashId)} />}
          </button>
        );
      })}
    </div>,
  );
});

ColorSelect.displayName = 'ColorSelect';

export default ColorSelect;
